import {buildContactUid, parseContactUid} from '../../../libs/shared/core/src/models/api/contactModel';
import {CardModel} from '../../models/api/cardModel';
import {CustomFieldModel} from '../../models/api/customFieldModel';

/*
 * Constants.
 */

export const addNewAccountAccountId = 0;

/*
 * Type enum.
 */

export enum CardAttributeUpdateActionsEnum {
  ADD_DESCRIPTION = 'ADD_DESCRIPTION',
  ADD_LINK = 'ADD_LINK',
  ADD_CUSTOM_ATTRIBUTE = 'ADD_CUSTOM_ATTRIBUTE',
  ADD_CONTACT = 'ADD_CONTACT',
  ADD_ACCOUNT = 'ADD_ACCOUNT',
  UPDATE_NAME = 'UPDATE_NAME',
  UPDATE_DESCRIPTION = 'UPDATE_DESCRIPTION',
  UPDATE_LINK = 'UPDATE_LINK',
  UPDATE_CUSTOM_ATTRIBUTE = 'UPDATE_CUSTOM_ATTRIBUTE',
  UPDATE_CONTACT = 'UPDATE_CONTACT',
  UPDATE_ACCOUNT = 'UPDATE_ACCOUNT',
  DELETE_DESCRIPTION = 'DELETE_DESCRIPTION',
  DELETE_LINK = 'DELETE_LINK',
  DELETE_CUSTOM_ATTRIBUTE = 'DELETE_CUSTOM_ATTRIBUTE',
  DELETE_CONTACT = 'DELETE_CONTACT',
  DELETE_ACCOUNT = 'DELETE_ACCOUNT',
}

/*
 * Action Types.
 */

// Adds
interface AddDescriptionAction {
  type: CardAttributeUpdateActionsEnum.ADD_DESCRIPTION;
  payload: {
    newAttributeValue: string;
  };
}
interface AddLinkAction {
  type: CardAttributeUpdateActionsEnum.ADD_LINK;
  payload: {
    newAttributeValue: string;
    index: number;
  };
}

interface AddCustomAttributeAction {
  type: CardAttributeUpdateActionsEnum.ADD_CUSTOM_ATTRIBUTE;
  payload: {
    newAttributeValue: string;
    customField: CustomFieldModel;
  };
}

interface AddContactAction {
  type: CardAttributeUpdateActionsEnum.ADD_CONTACT;
  payload: {
    contactUid: string;
  };
}

interface AddAccountAction {
  type: CardAttributeUpdateActionsEnum.ADD_ACCOUNT;
  payload: {
    newAttributeValue: number;
  };
}

// Updates
interface UpdateNameAction {
  type: CardAttributeUpdateActionsEnum.UPDATE_NAME;
  payload: {
    currentAttributeValue: string;
    newAttributeValue: string;
  };
}

interface UpdateDescriptionAction {
  type: CardAttributeUpdateActionsEnum.UPDATE_DESCRIPTION;
  payload: {
    currentAttributeValue: string;
    newAttributeValue: string;
  };
}

interface UpdateLinkAction {
  type: CardAttributeUpdateActionsEnum.UPDATE_LINK;
  payload: {
    currentAttributeValue: string;
    newAttributeValue: string;
    index: number;
  };
}

interface UpdateCustomAttributeAction {
  type: CardAttributeUpdateActionsEnum.UPDATE_CUSTOM_ATTRIBUTE;
  payload: {
    currentAttributeValue: string;
    newAttributeValue: string;
    customField: CustomFieldModel;
  };
}

interface UpdateContactAction {
  type: CardAttributeUpdateActionsEnum.UPDATE_CONTACT;
  payload: {
    currentAttributeValue: string;
    newAttributeValue: string;
    contactUid: string;
  };
}

interface UpdateAccountAction {
  type: CardAttributeUpdateActionsEnum.UPDATE_ACCOUNT;
  payload: {
    currentAttributeValue: number;
    newAttributeValue: number;
  };
}

// Deletes
interface DeleteDescriptionAction {
  type: CardAttributeUpdateActionsEnum.DELETE_DESCRIPTION;
  payload: {
    currentAttributeValue: string;
  };
}

interface DeleteLinkAction {
  type: CardAttributeUpdateActionsEnum.DELETE_LINK;
  payload: {
    currentAttributeValue: string;
    index: number;
  };
}

interface DeleteCustomAttributeAction {
  type: CardAttributeUpdateActionsEnum.DELETE_CUSTOM_ATTRIBUTE;
  payload: {
    currentAttributeValue: string;
    customField: CustomFieldModel;
  };
}

interface DeleteContactAction {
  type: CardAttributeUpdateActionsEnum.DELETE_CONTACT;
  payload: {
    contactUid: string;
  };
}

interface DeleteAccountAction {
  type: CardAttributeUpdateActionsEnum.DELETE_ACCOUNT;
  payload: {
    currentAttributeValue: number;
  };
}

/*
 * Action Union Type.
 */

export type CardAttributeAccountAction = AddAccountAction | UpdateAccountAction | DeleteAccountAction;

export type CardAttributeUpdateAction =
  | AddDescriptionAction
  | AddLinkAction
  | AddCustomAttributeAction
  | AddContactAction
  | UpdateNameAction
  | UpdateDescriptionAction
  | UpdateLinkAction
  | UpdateCustomAttributeAction
  | UpdateContactAction
  | DeleteDescriptionAction
  | DeleteLinkAction
  | DeleteCustomAttributeAction
  | DeleteContactAction
  | CardAttributeAccountAction;

/*
 * Action creators.
 */

export function addDescription(newDescription: string): AddDescriptionAction {
  return {
    type: CardAttributeUpdateActionsEnum.ADD_DESCRIPTION,
    payload: {
      newAttributeValue: newDescription,
    },
  };
}

export function addLink(newUrl: string, index: number): AddLinkAction {
  return {
    type: CardAttributeUpdateActionsEnum.ADD_LINK,
    payload: {
      newAttributeValue: newUrl,
      index,
    },
  };
}

export function addCustomAttribute(
  newValue: string,
  customField: CustomFieldModel,
): AddCustomAttributeAction {
  return {
    type: CardAttributeUpdateActionsEnum.ADD_CUSTOM_ATTRIBUTE,
    payload: {
      newAttributeValue: newValue,
      customField,
    },
  };
}

export function addContact(contactUid: string): AddContactAction {
  return {
    type: CardAttributeUpdateActionsEnum.ADD_CONTACT,
    payload: {
      contactUid,
    },
  };
}

export function addAccount(newAttributeValue: number): AddAccountAction {
  return {
    type: CardAttributeUpdateActionsEnum.ADD_ACCOUNT,
    payload: {
      newAttributeValue,
    },
  };
}

// Updates
export function updateName(currentName: string, newName: string): UpdateNameAction {
  return {
    type: CardAttributeUpdateActionsEnum.UPDATE_NAME,
    payload: {
      currentAttributeValue: currentName,
      newAttributeValue: newName,
    },
  };
}

export function updateDescription(
  currentDescription: string,
  newDescription: string,
): UpdateDescriptionAction {
  return {
    type: CardAttributeUpdateActionsEnum.UPDATE_DESCRIPTION,
    payload: {
      currentAttributeValue: currentDescription,
      newAttributeValue: newDescription,
    },
  };
}

export function updateLink(currentUrl: string, newUrl: string, index: number): UpdateLinkAction {
  return {
    type: CardAttributeUpdateActionsEnum.UPDATE_LINK,
    payload: {
      currentAttributeValue: currentUrl,
      newAttributeValue: newUrl,
      index,
    },
  };
}

export function updateCustomAttribute(
  currentValue: string,
  newValue: string,
  customField: CustomFieldModel,
): UpdateCustomAttributeAction {
  return {
    type: CardAttributeUpdateActionsEnum.UPDATE_CUSTOM_ATTRIBUTE,
    payload: {
      currentAttributeValue: currentValue,
      newAttributeValue: newValue,
      customField,
    },
  };
}

export function updateContact(
  currentHandle: string,
  newHandle: string,
  contactUid: string,
): UpdateContactAction {
  return {
    type: CardAttributeUpdateActionsEnum.UPDATE_CONTACT,
    payload: {
      currentAttributeValue: currentHandle,
      newAttributeValue: newHandle,
      contactUid,
    },
  };
}

export function updateAccount(currentAttributeValue: number, newAttributeValue: number): UpdateAccountAction {
  return {
    type: CardAttributeUpdateActionsEnum.UPDATE_ACCOUNT,
    payload: {
      currentAttributeValue,
      newAttributeValue,
    },
  };
}

// Deletes
export function deleteDescription(currentDescription: string): DeleteDescriptionAction {
  return {
    type: CardAttributeUpdateActionsEnum.DELETE_DESCRIPTION,
    payload: {
      currentAttributeValue: currentDescription,
    },
  };
}

export function deleteLink(currentUrl: string, index: number): DeleteLinkAction {
  return {
    type: CardAttributeUpdateActionsEnum.DELETE_LINK,
    payload: {
      currentAttributeValue: currentUrl,
      index,
    },
  };
}

export function deleteCustomAttribute(
  currentValue: string,
  customField: CustomFieldModel,
): DeleteCustomAttributeAction {
  return {
    type: CardAttributeUpdateActionsEnum.DELETE_CUSTOM_ATTRIBUTE,
    payload: {
      currentAttributeValue: currentValue,
      customField,
    },
  };
}

export function deleteContact(contactUid: string): DeleteContactAction {
  return {
    type: CardAttributeUpdateActionsEnum.DELETE_CONTACT,
    payload: {
      contactUid,
    },
  };
}

export function deleteAccount(currentAccountId: number): DeleteAccountAction {
  return {
    type: CardAttributeUpdateActionsEnum.DELETE_ACCOUNT,
    payload: {
      currentAttributeValue: currentAccountId,
    },
  };
}

/*
 * Reducer.
 */

export function cardAttributeUpdateReducer(existingCard: CardModel, action: CardAttributeUpdateAction) {
  switch (action.type) {
    // Adds
    case CardAttributeUpdateActionsEnum.ADD_DESCRIPTION:
      return {
        ...existingCard,
        description: action.payload.newAttributeValue,
      };
    case CardAttributeUpdateActionsEnum.ADD_LINK:
      return {
        ...existingCard,
        links: [...existingCard.links, {url: action.payload.newAttributeValue}],
      };
    case CardAttributeUpdateActionsEnum.ADD_CUSTOM_ATTRIBUTE:
      return {
        ...existingCard,
        customFieldAttributes: [
          ...(existingCard.customFieldAttributes ?? []),
          {id: action.payload.customField.id, value: action.payload.newAttributeValue},
        ],
      };
    case CardAttributeUpdateActionsEnum.ADD_CONTACT:
      return {
        ...existingCard,
        contactUids: [...existingCard.contactUids, action.payload.contactUid],
      };
    case CardAttributeUpdateActionsEnum.ADD_ACCOUNT:
      return {
        ...existingCard,
        accountId: action.payload.newAttributeValue,
      };

    // Updates
    case CardAttributeUpdateActionsEnum.UPDATE_NAME:
      return {
        ...existingCard,
        name: action.payload.newAttributeValue,
      };
    case CardAttributeUpdateActionsEnum.UPDATE_DESCRIPTION:
      return {
        ...existingCard,
        description: action.payload.newAttributeValue,
      };
    case CardAttributeUpdateActionsEnum.UPDATE_LINK:
      return {
        ...existingCard,
        links: existingCard.links.map((link, index) =>
          index === action.payload.index ? {...link, url: action.payload.newAttributeValue} : link,
        ),
      };
    case CardAttributeUpdateActionsEnum.UPDATE_CUSTOM_ATTRIBUTE:
      return {
        ...existingCard,
        customFieldAttributes: existingCard.customFieldAttributes?.map((customAttribute) =>
          customAttribute.id === action.payload.customField.id
            ? {...customAttribute, value: action.payload.newAttributeValue}
            : customAttribute,
        ),
      };
    case CardAttributeUpdateActionsEnum.UPDATE_CONTACT:
      return {
        ...existingCard,
        contactUids: computeUpdatedContactUids(existingCard, action),
      };
    case CardAttributeUpdateActionsEnum.UPDATE_ACCOUNT:
      return {
        ...existingCard,
        accountId: action.payload.newAttributeValue,
      };

    // Deletes
    case CardAttributeUpdateActionsEnum.DELETE_DESCRIPTION:
      return {
        ...existingCard,
        description: undefined,
      };
    case CardAttributeUpdateActionsEnum.DELETE_LINK:
      return {
        ...existingCard,
        links: existingCard.links.filter((_link, index) => index !== action.payload.index),
      };
    case CardAttributeUpdateActionsEnum.DELETE_CUSTOM_ATTRIBUTE:
      return {
        ...existingCard,
        customFieldAttributes: existingCard.customFieldAttributes?.filter(
          (customAttribute) => customAttribute.id !== action.payload.customField.id,
        ),
      };
    case CardAttributeUpdateActionsEnum.DELETE_CONTACT:
      return {
        ...existingCard,
        contactUids: existingCard.contactUids.filter(
          (contactUid) => contactUid !== action.payload.contactUid,
        ),
      };
    case CardAttributeUpdateActionsEnum.DELETE_ACCOUNT:
      return {
        ...existingCard,
        accountId: undefined,
      };
    default: {
      return existingCard;
    }
  }
}

function computeUpdatedContactUids(existingCard: CardModel, action: UpdateContactAction) {
  // Build a new contactUid string with the same source but new handle value
  const contactUidToUpdate = action.payload.contactUid;
  const [source] = parseContactUid(contactUidToUpdate);
  const newContactUid = buildContactUid(source, action.payload.newAttributeValue);

  // Replace the old contactUid string with the new contactUid string
  return existingCard.contactUids.map((contactUid) =>
    contactUid === contactUidToUpdate ? newContactUid : contactUid,
  );
}

/*
 * Inverse actions for undos.
 */

export function computeInverseCardAttributeUpdateAction(action: CardAttributeUpdateAction) {
  switch (action.type) {
    case CardAttributeUpdateActionsEnum.ADD_DESCRIPTION:
      return deleteDescription(action.payload.newAttributeValue);
    case CardAttributeUpdateActionsEnum.ADD_LINK:
      return deleteLink(action.payload.newAttributeValue, action.payload.index);
    case CardAttributeUpdateActionsEnum.ADD_CUSTOM_ATTRIBUTE:
      return deleteCustomAttribute(action.payload.newAttributeValue, action.payload.customField);
    case CardAttributeUpdateActionsEnum.ADD_CONTACT:
      return deleteContact(action.payload.contactUid);
    case CardAttributeUpdateActionsEnum.ADD_ACCOUNT:
      return deleteAccount(action.payload.newAttributeValue);

    case CardAttributeUpdateActionsEnum.UPDATE_NAME:
      return updateName(action.payload.newAttributeValue, action.payload.currentAttributeValue);
    case CardAttributeUpdateActionsEnum.UPDATE_DESCRIPTION:
      return updateDescription(action.payload.newAttributeValue, action.payload.currentAttributeValue);
    case CardAttributeUpdateActionsEnum.UPDATE_LINK:
      return updateLink(
        action.payload.newAttributeValue,
        action.payload.currentAttributeValue,
        action.payload.index,
      );
    case CardAttributeUpdateActionsEnum.UPDATE_CUSTOM_ATTRIBUTE:
      return updateCustomAttribute(
        action.payload.newAttributeValue,
        action.payload.currentAttributeValue,
        action.payload.customField,
      );
    case CardAttributeUpdateActionsEnum.UPDATE_CONTACT:
      return updateContact(
        action.payload.newAttributeValue,
        action.payload.currentAttributeValue,
        action.payload.contactUid,
      );
    case CardAttributeUpdateActionsEnum.UPDATE_ACCOUNT:
      return updateAccount(action.payload.newAttributeValue, action.payload.currentAttributeValue);

    case CardAttributeUpdateActionsEnum.DELETE_DESCRIPTION:
      return addDescription(action.payload.currentAttributeValue);
    case CardAttributeUpdateActionsEnum.DELETE_LINK:
      return addLink(action.payload.currentAttributeValue, action.payload.index);
    case CardAttributeUpdateActionsEnum.DELETE_CUSTOM_ATTRIBUTE:
      return addCustomAttribute(action.payload.currentAttributeValue, action.payload.customField);
    case CardAttributeUpdateActionsEnum.DELETE_CONTACT:
      return addContact(action.payload.contactUid);
    case CardAttributeUpdateActionsEnum.DELETE_ACCOUNT:
      return addAccount(action.payload.currentAttributeValue);
    default:
      return action;
  }
}

export const actionIsCardAccountAction = (
  action: CardAttributeUpdateAction,
): action is CardAttributeAccountAction =>
  action.type === CardAttributeUpdateActionsEnum.ADD_ACCOUNT ||
  action.type === CardAttributeUpdateActionsEnum.UPDATE_ACCOUNT ||
  action.type === CardAttributeUpdateActionsEnum.DELETE_ACCOUNT;
